(() => {
  'use strict';

  const root = document.querySelector('[data-fast-order]');
  if (!root) return;

  const GA4_ID = 'G-1DPH44QH6N';
  const MAX_LINES = 100;
  const SEARCH_LIMIT = 10;
  const rowsElement = root.querySelector('[data-rows]');
  const addButton = root.querySelector('[data-action="add-to-cart"]');
  const summary = root.querySelector('[data-summary]');
  const message = root.querySelector('[data-message]');
  const pastePanel = root.querySelector('[data-paste-panel]');
  const pasteInput = root.querySelector('[data-paste-input]');
  const cartLink = root.querySelector('[data-cart-link]');
  const rowData = new Map();
  const lookupCache = new Map();
  let nextId = 0;
  let justAdded = false;

  const storefrontRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const normalizeSku = value => String(value || '').trim().toUpperCase();
  const isThemePreview = new URLSearchParams(window.location.search).has('preview_theme_id') ||
    Boolean(window.Shopify && window.Shopify.designMode);
  const track = (eventName, parameters = {}) => {
    if (!isThemePreview && typeof window.gtag === 'function') {
      window.gtag('event', eventName, { send_to: GA4_ID, ...parameters });
    }
  };
  const sourceForRows = rows => {
    const sources = [...new Set(rows.map(row => row.source).filter(Boolean))];
    return sources.length === 1 ? sources[0] : (sources.length ? 'mixed' : 'manual');
  };
  const setMessage = (text, state = '') => {
    message.textContent = text;
    message.dataset.state = state;
  };
  const safeInt = value => {
    const n = Number(value);
    return Number.isSafeInteger(n) && n > 0 ? n : 0;
  };

  function addRow(sku = '', quantity = 1, source = 'manual') {
    if (rowData.size >= MAX_LINES) {
      setMessage(`You can add up to ${MAX_LINES} lines at a time.`, 'error');
      return null;
    }
    justAdded = false;
    const id = String(++nextId);
    const tr = document.createElement('tr');
    tr.dataset.rowId = id;
    tr.innerHTML = `
      <td><input type="text" inputmode="text" autocomplete="off" data-field="sku" aria-label="Product code or SKU" placeholder="Enter SKU"></td>
      <td><input type="number" min="1" step="1" value="1" data-field="quantity" aria-label="Quantity"></td>
      <td><span class="fast-order__product" data-product></span><span class="fast-order__product-meta" data-product-meta></span></td>
      <td><span class="fast-order__row-status" data-status data-state="empty">Enter a SKU</span></td>
      <td><button class="fast-order__remove" type="button" data-action="remove-row" aria-label="Remove line">×</button></td>`;
    const skuInput = tr.querySelector('[data-field="sku"]');
    const qtyInput = tr.querySelector('[data-field="quantity"]');
    skuInput.value = sku;
    const quantityText = quantity === undefined || quantity === null ? '' : String(quantity).trim();
    const initialQuantity = quantityText ? safeInt(quantityText) : 1;
    qtyInput.value = initialQuantity ? String(initialQuantity) : '';
    rowsElement.appendChild(tr);
    const row = { id, tr, sku: String(sku).trim(), quantity: initialQuantity, source, product: null, variant: null, status: sku ? 'pending' : 'empty', timer: null, requestId: 0 };
    rowData.set(id, row);
    if (sku) updateLookup(row, true);
    updateSummary();
    return row;
  }

  function setRowStatus(row, text, state) {
    const status = row.tr.querySelector('[data-status]');
    status.textContent = text;
    status.dataset.state = state;
    row.status = state;
  }

  function updateSummary() {
    const active = [...rowData.values()].filter(row => row.sku || row.status === 'pending');
    const ready = active.filter(row => row.status === 'ready' && safeInt(row.quantity));
    const waiting = active.some(row => row.status === 'pending');
    const invalid = active.some(row => row.status === 'error' || !safeInt(row.quantity));
    const totalQty = ready.reduce((total, row) => total + safeInt(row.quantity), 0);
    if (!active.length) summary.textContent = 'Add a SKU to see the product match.';
    else if (waiting) summary.textContent = `Checking ${active.length} ${active.length === 1 ? 'line' : 'lines'}…`;
    else if (invalid) summary.textContent = `${ready.length} ready · fix the highlighted lines before adding.`;
    else summary.textContent = `${ready.length} ${ready.length === 1 ? 'item' : 'items'} · ${totalQty} total quantity`;
    addButton.disabled = justAdded || !active.length || waiting || invalid || ready.length !== active.length;
  }

  async function resolveSku(sku) {
    const normalized = normalizeSku(sku);
    if (!normalized) return { status: 'empty' };
    if (lookupCache.has(normalized)) return lookupCache.get(normalized);

    const promise = (async () => {
      const params = new URLSearchParams({
        q: sku.trim(),
        'resources[type]': 'product',
        'resources[limit]': String(SEARCH_LIMIT),
        'resources[options][fields]': 'variants.sku,variants.title,title'
      });
      const searchResponse = await fetch(`${storefrontRoot}search/suggest.json?${params.toString()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!searchResponse.ok) throw new Error('Product search is temporarily unavailable. Please try again.');
      const data = await searchResponse.json();
      const candidates = data?.resources?.results?.products || [];
      for (const candidate of candidates.slice(0, SEARCH_LIMIT)) {
        if (!candidate.url) continue;
        const candidateUrl = new URL(candidate.url, window.location.origin);
        if (!candidateUrl.pathname.includes('/products/')) continue;
        const productResponse = await fetch(`${candidateUrl.pathname}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!productResponse.ok) continue;
        const product = await productResponse.json();
        const variant = (product.variants || []).find(item => normalizeSku(item.sku) === normalized);
        if (variant) return { status: variant.available ? 'ready' : 'unavailable', product, variant };
      }
      return { status: 'not_found' };
    })();
    lookupCache.set(normalized, promise);
    try {
      return await promise;
    } catch (error) {
      lookupCache.delete(normalized);
      throw error;
    }
  }

  async function updateLookup(row, immediate = false) {
    if (row.timer) clearTimeout(row.timer);
    const typedSku = row.tr.querySelector('[data-field="sku"]').value.trim();
    row.sku = typedSku;
    row.product = null;
    row.variant = null;
    row.requestId += 1;
    const currentRequestId = row.requestId;
    const productName = row.tr.querySelector('[data-product]');
    const meta = row.tr.querySelector('[data-product-meta]');
    productName.textContent = '';
    meta.textContent = '';
    if (!typedSku) {
      setRowStatus(row, 'Enter a SKU', 'empty');
      updateSummary();
      return;
    }
    setRowStatus(row, 'Checking…', 'pending');
    updateSummary();

    const run = async () => {
      try {
        const result = await resolveSku(typedSku);
        if (currentRequestId !== row.requestId) return;
        if (result.status === 'not_found') {
          setRowStatus(row, 'No exact SKU match', 'error');
          track('fast_order_lookup', { result: 'not_found', input_method: row.source });
        } else {
          row.product = result.product;
          row.variant = result.variant;
          productName.textContent = result.product.title || '';
          const variantLabel = result.variant.title && result.variant.title !== 'Default Title' ? ` · ${result.variant.title}` : '';
          meta.textContent = `${result.variant.sku || typedSku}${variantLabel}`;
          if (result.status === 'unavailable') {
            setRowStatus(row, 'Unavailable to order', 'error');
            track('fast_order_lookup', { result: 'unavailable', input_method: row.source });
          } else if (!safeInt(row.quantity)) {
            setRowStatus(row, 'Enter a quantity of 1 or more', 'error');
            track('fast_order_lookup', { result: 'found', input_method: row.source });
          } else {
            setRowStatus(row, 'Matched', 'ready');
            track('fast_order_lookup', { result: 'found', input_method: row.source });
          }
        }
      } catch (error) {
        if (currentRequestId !== row.requestId) return;
        setRowStatus(row, 'Lookup failed · try again', 'error');
        setMessage(error.message || 'We could not check that SKU. Please try again.', 'error');
        track('fast_order_lookup', { result: 'error', input_method: row.source });
      }
      updateSummary();
    };
    if (immediate) await run();
    else row.timer = window.setTimeout(run, 300);
  }

  function parseCsv(text) {
    const rows = [];
    let fields = [], value = '', quoted = false;
    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i];
      if (ch === '"') {
        if (quoted && text[i + 1] === '"') { value += '"'; i += 1; }
        else quoted = !quoted;
      } else if (!quoted && (ch === '\n' || ch === '\r')) {
        if (ch === '\r' && text[i + 1] === '\n') i += 1;
        fields.push(value.trim());
        if (fields.some(Boolean)) rows.push(fields);
        fields = []; value = '';
      } else if (!quoted && (ch === '\t' || ch === ',' || ch === ';')) {
        fields.push(value.trim()); value = '';
      } else value += ch;
    }
    fields.push(value.trim());
    if (fields.some(Boolean)) rows.push(fields);
    if (!rows.length) return [];

    const first = rows[0].map(cell => cell.toLowerCase().replace(/[^a-z]/g, ''));
    const skuNames = ['sku', 'productcode', 'code', 'itemcode', 'productsku'];
    const qtyNames = ['qty', 'quantity', 'units'];
    const skuIndex = first.findIndex(cell => skuNames.includes(cell));
    const qtyIndex = first.findIndex(cell => qtyNames.includes(cell));
    const hasHeader = skuIndex >= 0 || qtyIndex >= 0;
    const start = hasHeader ? 1 : 0;
    const selectedSkuIndex = skuIndex >= 0 ? skuIndex : 0;
    const selectedQtyIndex = qtyIndex >= 0 ? qtyIndex : 1;
    return rows.slice(start).map(cells => {
      let sku = cells[selectedSkuIndex] || '';
      let quantity = cells[selectedQtyIndex] === undefined || cells[selectedQtyIndex] === '' ? '1' : cells[selectedQtyIndex];
      if (cells.length === 1) {
        const match = sku.match(/^(\S+)\s+(\d+)$/);
        if (match) { sku = match[1]; quantity = match[2]; }
      }
      return { sku: String(sku).trim(), quantity: String(quantity).trim() || '1' };
    }).filter(line => line.sku);
  }

  async function importLines(lines, source) {
    if (!lines.length) {
      setMessage('No product SKUs were found in that list.', 'error');
      return;
    }
    if (lines.length > MAX_LINES) {
      setMessage(`This import has ${lines.length} lines. Please split it into files of ${MAX_LINES} lines or fewer.`, 'error');
      return;
    }
    justAdded = false;
    for (const row of rowData.values()) {
      if (row.timer) clearTimeout(row.timer);
      row.requestId += 1;
    }
    rowData.clear();
    rowsElement.replaceChildren();
    setMessage(`Checking ${lines.length} lines…`);
    const rows = lines.map(line => addRow(line.sku, line.quantity, source)).filter(Boolean);
    let index = 0;
    const worker = async () => {
      while (index < rows.length) {
        const row = rows[index++];
        await updateLookup(row, true);
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, rows.length) }, worker));
    const failures = rows.filter(row => row.status === 'error').length;
    setMessage(failures ? `${rows.length - failures} matched · ${failures} need attention. No items have been added yet.` : `${rows.length} lines checked and ready to add.`, failures ? 'error' : 'success');
    track('fast_order_submit', { input_method: source, line_count: rows.length, result: failures ? 'validation_error' : 'validated' });
    const first = rowsElement.querySelector('[data-field="sku"]');
    if (first) first.focus({ preventScroll: true });
  }

  root.addEventListener('click', async event => {
    const actionButton = event.target.closest('[data-action]');
    if (!actionButton) return;
    const action = actionButton.dataset.action;
    if (action === 'toggle-paste') {
      const willOpen = pastePanel.hidden;
      pastePanel.hidden = !willOpen;
      actionButton.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) pasteInput.focus();
    } else if (action === 'cancel-paste') {
      pastePanel.hidden = true;
      root.querySelector('[data-action="toggle-paste"]').setAttribute('aria-expanded', 'false');
    } else if (action === 'import-paste') {
      const lines = parseCsv(pasteInput.value);
      if (!lines.length) { setMessage('Paste at least one SKU before adding lines.', 'error'); return; }
      pastePanel.hidden = true;
      root.querySelector('[data-action="toggle-paste"]').setAttribute('aria-expanded', 'false');
      await importLines(lines, 'paste');
    } else if (action === 'add-row') {
      const row = addRow();
      if (row) row.tr.querySelector('[data-field="sku"]').focus();
    } else if (action === 'remove-row') {
      const tr = actionButton.closest('[data-row-id]');
      const row = rowData.get(tr?.dataset.rowId);
      if (row?.timer) clearTimeout(row.timer);
      if (row) rowData.delete(row.id);
      tr?.remove();
      justAdded = false;
      if (!rowData.size) addRow();
      updateSummary();
    } else if (action === 'add-to-cart') {
      const lines = [...rowData.values()].filter(row => row.sku);
      if (!lines.length || lines.some(row => row.status !== 'ready' || !safeInt(row.quantity))) {
        setMessage('Match every SKU and check each quantity before adding to your basket.', 'error');
        updateSummary();
        return;
      }
      const combined = new Map();
      for (const row of lines) {
        const key = String(row.variant.id);
        const current = combined.get(key) || { id: row.variant.id, quantity: 0, variant: row.variant, row };
        current.quantity += safeInt(row.quantity);
        combined.set(key, current);
      }
      const cartItems = [...combined.values()];
      const totalQty = cartItems.reduce((total, item) => total + item.quantity, 0);
      const inputMethod = sourceForRows(lines);
      addButton.disabled = true;
      addButton.textContent = 'Adding…';
      setMessage('Adding items to your basket…');
      track('fast_order_submit', { input_method: inputMethod, line_count: lines.length, total_quantity: totalQty, result: 'submitted' });
      try {
        const response = await fetch(`${storefrontRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: cartItems.map(({ id, quantity }) => ({ id, quantity })) })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'We could not add these items. Please check stock and quantities.');
        for (const item of cartItems) {
          document.documentElement.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: { variant: item.variant, quantity: item.quantity, button: addButton }
          }));
        }
        justAdded = true;
        setMessage(`${lines.length} ${lines.length === 1 ? 'line has' : 'lines have'} been added to your basket.`, 'success');
        cartLink.hidden = false;
        track('fast_order_add_to_cart', { input_method: inputMethod, line_count: lines.length, unique_items: cartItems.length, total_quantity: totalQty });
      } catch (error) {
        setMessage(error.message || 'Something went wrong while adding to basket. Please try again.', 'error');
        track('fast_order_add_to_cart', { input_method: inputMethod, line_count: lines.length, total_quantity: totalQty, result: 'error' });
      } finally {
        addButton.textContent = 'Add to basket';
        updateSummary();
      }
    }
  });

  root.addEventListener('input', event => {
    const input = event.target;
    const tr = input.closest('[data-row-id]');
    if (!tr) return;
    const row = rowData.get(tr.dataset.rowId);
    if (!row) return;
    if (input.dataset.field === 'sku') {
      justAdded = false;
      row.source = 'manual';
      row.sku = input.value.trim();
      updateLookup(row);
    } else if (input.dataset.field === 'quantity') {
      justAdded = false;
      row.quantity = safeInt(input.value);
      if (!row.quantity) setRowStatus(row, 'Enter a quantity of 1 or more', 'error');
      else if (row.product && row.variant && row.variant.available) setRowStatus(row, 'Matched', 'ready');
      updateSummary();
    }
  });

  root.querySelector('[data-file-input]').addEventListener('change', async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      await importLines(parseCsv(text), 'csv');
    } catch (error) {
      setMessage('We could not read that file. Please choose a valid CSV or text file.', 'error');
    } finally {
      event.target.value = '';
    }
  });

  for (let i = 0; i < 5; i += 1) addRow();
  track('fast_order_view', { page_location: window.location.href });
})();
